/* Homepage motion layer: gives each existing section its own entrance and text effect, without changing layout.
   If anything here fails, the "rook-motion" class is removed and the page shows exactly as it does without this file. */
(() => {
  const root = document.documentElement;
  // "New Collection" banner: a click anywhere on a slide opens that slide's own link (its button). This sits
  // before the motion checks below so it works with reduced motion too. A drag of the slider is not a click.
  if (!window.rookSlideLinks) {
    window.rookSlideLinks = true;
    let pressedAt = null;
    document.addEventListener('pointerdown', (event) => { pressedAt = [event.clientX, event.clientY]; }, true);
    document.addEventListener('click', (event) => {
      const slide = event.target.closest ? event.target.closest('.rook-banner-with-slider .rook-slide') : null;
      if (!slide || event.target.closest('a, button')) return;
      if (pressedAt && Math.hypot(event.clientX - pressedAt[0], event.clientY - pressedAt[1]) > 8) return;
      const link = slide.querySelector('.rook-slide__button a[href]');
      if (link && link.getAttribute('href') !== '#') window.location.href = link.href;
    });
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) return;

  const MAX_STAGGER = 6;
  const CLIP_FX = ['wipe-right', 'circle', 'diagonal'];
  const THEME_TRIGGER = ['rook-scroll-trigger', 'animate--fade-in-up', 'rook-scroll-trigger--offscreen'];

  const init = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;
    root.classList.add('rook-motion');
    const all = (selector, scope = main) => Array.from(scope.querySelectorAll(selector));

    // The list an item belongs to: the nearest ancestor in which it has neighbours of the same kind
    const listOf = (el, selector) => {
      let node = el;
      for (let depth = 0; depth < 4 && node.parentElement && node.parentElement !== main; depth++) {
        const siblings = Array.from(node.parentElement.children).filter((child) => child.matches(selector) || child.querySelector(selector));
        if (siblings.length > 1) {
          const list = node.parentElement;
          // a slider's inner wrapper moves off-screen as it slides; its outer box stays put
          return { list: list.classList.contains('swiper-wrapper') && list.parentElement ? list.parentElement : list, index: siblings.indexOf(node) };
        }
        node = node.parentElement;
      }
      return { list: el, index: 0 };
    };

    // ---------- text ----------
    // Wrap words (and optionally letters) in spans, keeping any <br> in place
    const splitText = (el, withChars) => {
      const label = el.textContent.replace(/\s+/g, ' ').trim();
      if (!label) return false;
      let wordIndex = 0;
      let charIndex = 0;
      let lastChar = null;
      const walk = (node) => {
        if (node.nodeType === 3) {
          const fragment = document.createDocumentFragment();
          node.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              fragment.appendChild(document.createTextNode(' '));
              return;
            }
            const word = document.createElement('span');
            word.className = 'rook-w';
            word.setAttribute('aria-hidden', 'true');
            word.style.setProperty('--rook-i', wordIndex++);
            if (withChars) {
              Array.from(part).forEach((letter) => {
                const char = document.createElement('span');
                char.className = 'rook-c';
                char.style.setProperty('--rook-i', charIndex++);
                char.textContent = letter;
                word.appendChild(char);
                lastChar = char;
              });
            } else {
              word.textContent = part;
            }
            fragment.appendChild(word);
          });
          node.replaceWith(fragment);
        } else if (node.nodeType === 1 && node.tagName !== 'BR') {
          Array.from(node.childNodes).forEach(walk);
        }
      };
      Array.from(el.childNodes).forEach(walk);
      if (lastChar) lastChar.classList.add('rook-c--last');
      el.setAttribute('aria-label', label);
      return true;
    };
    const splitLines = (el) => {
      const lines = el.innerHTML.split(/<br\s*\/?>/i).map((line) => line.trim()).filter(Boolean);
      if (!lines.length) return false;
      el.innerHTML = lines.map((line, i) => `<span class="rook-line"><span style="--rook-i: ${i}">${line}</span></span>`).join('');
      return true;
    };

    // effect: type | blur | wipe | track | flip | focus | words
    const textFx = (selector, effect, options = {}) => {
      all(selector).forEach((el) => {
        if (el.dataset.rookText) return;
        if (el.querySelector(':scope > :not(br)')) return; // leave anything with real markup inside alone
        if (effect === 'track') el.style.setProperty('--rook-ls', window.getComputedStyle(el).letterSpacing);
        let ready = true;
        if (effect === 'type' || effect === 'blur') ready = splitText(el, true);
        else if (effect === 'flip' || effect === 'words') ready = splitText(el, false);
        else if (effect === 'wipe') ready = splitLines(el);
        if (!ready) return;
        if (options.delay) el.style.setProperty('--rook-delay', options.delay);
        if (options.step) el.style.setProperty('--rook-step', options.step);
        el.classList.remove(...THEME_TRIGGER); // this text now has its own entrance
        el.dataset.rookText = effect;
        // text inside a slider plays when its slide becomes active; everything else plays on scroll
        if (!el.closest('.swiper-slide')) el.classList.add('rook-watch');
      });
    };

    const imageCardSections = () => all('.rook-custom-content').filter((section) => section.querySelector('.rook-image-card'));

    const assignText = () => {
      textFx('.rook-slideshow-section .rook-slide__subtitle', 'type', { delay: '0.35s' });
      textFx('.rook-slideshow-section .rook-slide__title', 'blur', { delay: '0.55s' });
      textFx('.rook-banner-with-slider .rook-slide__title', 'wipe');
      textFx('.rook-product-tabs .rook-section__heading', 'type', { step: '60ms' });
      textFx('.rook-countdown__heading', 'focus');
      textFx('.rook-lookbook__heading', 'flip');
      textFx('.shoppable-reels__heading', 'track');
      textFx('.rook-testimonials .rook-section__heading', 'words');
      // image-card headings: first pair closes in from wide spacing, second pair drifts in word by word
      imageCardSections().forEach((section, i) => {
        all('.rook-image-card__heading', section).forEach((heading) => {
          heading.dataset.rookPick = i % 2 === 0 ? 'track' : 'words';
        });
      });
      textFx('.rook-image-card__heading[data-rook-pick="track"]', 'track');
      textFx('.rook-image-card__heading[data-rook-pick="words"]', 'words');
      // any other section heading gets the word entrance
      textFx('.rook-section__heading', 'words');
    };
    assignText();

    // ---------- blocks ----------
    // effect: wipe-right | circle | unfold | diagonal | slide-left | slide-right | zoom | deal
    const blockFx = (selector, effect, options = {}) => {
      all(selector).forEach((el, order) => {
        const target = options.inner ? el.querySelector(options.inner) : el;
        if (!target || target.dataset.rookFx) return;
        const group = listOf(el, selector);
        target.dataset.rookFx = effect;
        target.style.setProperty('--rook-i', (options.stagger === false ? 0 : group.index) % MAX_STAGGER);
        if (effect === 'deal') target.style.setProperty('--rook-rot', order % 2 === 0 ? '-6deg' : '6deg');
        // a fully clipped element never counts as on screen, so watch its parent instead
        const selfClipped = CLIP_FX.includes(effect) && target === el && el.parentElement;
        (options.group ? group.list : selfClipped ? el.parentElement : el).classList.add('rook-watch');
      });
    };

    const assignBlocks = () => {
      // "New Collection" banner: side cards glide in from their own side, the centre grows out
      blockFx('.rook-banner-with-slider__banner--medium a.rook-hover-box', 'slide-left', { stagger: false });
      blockFx('.rook-banner-with-slider__slider', 'unfold', { stagger: false });
      blockFx('.rook-banner-with-slider__banner-group a.rook-hover-box', 'slide-right');
      // product grid: photos are uncovered from the left, one after another
      blockFx('.rook-product-tabs .rook-product-card', 'wipe-right', { inner: '.rook-product-card__media', group: true });
      // image cards: first pair comes in from opposite sides, second pair grows out of a small frame
      imageCardSections().forEach((section, i) => {
        all('.rook-image-card', section).forEach((card, n) => {
          if (card.classList.contains('rook-watch')) return;
          if (i % 2 === 0) {
            card.dataset.rookFx = n % 2 === 0 ? 'slide-left' : 'slide-right';
          } else {
            const image = card.querySelector('.rook-image-card__img');
            if (image) {
              image.dataset.rookFx = 'unfold';
              image.style.setProperty('--rook-i', n);
            }
          }
          card.classList.add('rook-watch');
        });
      });
      // countdown: the timer grows in, its picture is swept open on a slant
      blockFx('.rook-custom-content--countdown .rook-countdown__inner', 'zoom', { stagger: false });
      blockFx('.rook-custom-content--countdown .rook-image-block', 'diagonal', { stagger: false });
      // lookbook: the photo opens from its centre, the products glide in from the right
      blockFx('.rook-lookbook-card__image', 'circle', { stagger: false });
      blockFx('.rook-lookbook-slider__inner', 'slide-right', { stagger: false });
      // reels: cards are dealt out, each tilted the other way
      blockFx('.shoppable-reels__card', 'deal', { group: true });
      // reviews: cards glide in from the right, one after another
      blockFx('.rook-testimonial__wrapper', 'slide-right', { group: true });
    };
    assignBlocks();

    // ---------- one observer for everything above ----------
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          el.classList.add('rook-inview', 'rook-play');
          observer.unobserve(el);
          if (el.dataset.rookText === 'type') {
            // once the line has been typed (and the caret has blinked), clear every caret for good
            const style = window.getComputedStyle(el);
            const step = parseFloat(style.getPropertyValue('--rook-step')) || 55;
            const delay = (parseFloat(style.getPropertyValue('--rook-delay')) || 0) * 1000;
            const letters = el.querySelectorAll('.rook-c').length;
            window.setTimeout(() => el.classList.add('rook-typed'), delay + letters * step + 2800);
          }
          const clipped = (el.dataset.rookFx ? [el] : []).concat(all('[data-rook-fx]', el));
          if (clipped.length) window.setTimeout(() => clipped.forEach((node) => node.classList.add('rook-done')), 2600);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
    );
    const watchAll = () => all('.rook-watch:not(.rook-inview)').forEach((el) => observer.observe(el));
    watchAll();
    // sliders clone and rebuild their slides after start-up, so set up and look again once things have settled
    window.addEventListener('load', () => {
      [300, 1500, 4000].forEach((delay) =>
        window.setTimeout(() => {
          assignText();
          assignBlocks();
          watchAll();
        }, delay)
      );
    });

    // ---------- photos that drift with the scroll (desktop only) ----------
    if (!window.matchMedia('(hover: hover) and (min-width: 990px)').matches) return;
    const largestImage = (box) => {
      let best = null;
      let bestArea = 0;
      box.querySelectorAll('img, svg').forEach((node) => {
        const rect = node.getBoundingClientRect();
        const area = rect.width * rect.height;
        if (area > bestArea) {
          best = node;
          bestArea = area;
        }
      });
      return best;
    };
    const frames = [];
    all('.rook-image-card__img, .rook-banner-with-slider__banner a.rook-hover-box, .rook-image-block').forEach((box) => {
      const image = largestImage(box);
      if (!image) return;
      // not "rook-parallax": the theme has its own "rook-parallax" class, which turns clicks off
      box.classList.add('rook-drift');
      // the theme's load-in animation keeps hold of the <img> transform, so move its wrapper instead
      (image.closest('responsive-image') || image).classList.add('rook-drift__img');
      frames.push(box);
    });
    if (!frames.length) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const viewport = window.innerHeight;
      frames.forEach((box) => {
        const rect = box.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;
        const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
        // the picture is 10% larger than its frame: never travel further than that spare edge
        const limit = Math.max(0, rect.height * 0.05 - 1);
        const shift = Math.max(-limit, Math.min(limit, progress * -30));
        box.style.setProperty('--rook-shift', `${shift.toFixed(1)}px`);
      });
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  };

  const start = () => {
    try {
      init();
    } catch (error) {
      root.classList.remove('rook-motion');
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
